import { useEffect, useId, useMemo, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { api, errorText } from '../lib/api';
import { useMe } from '../lib/session';
import { dirClass, dirLabel, fmtR, fmtTime, titleCase } from '../lib/format';
import type { Instrument, TradeView } from '../lib/types';
import type { SetupStatus } from '../lib/types-setups';
import { Button, Card, cx, Empty, ErrorText, PageHeader, Segmented, Select } from '../components/ui';
import { Avatar, CommentThread, DiscussToggle, EntityMenu, FollowButton, ReactionBar, useFollows, useMembers, type SocialSummary, type TargetType } from '../components/social';
import { SetupStatusPill, zoneText } from './Setups';
import { isLibraryFeed, LibraryFeedItem } from '../components/library/LibraryFeedItem';
import { isFlashing, isSignalFeed, SignalFeedItem, useFlashClock } from '../components/SignalFeedItem';
import { SignalTable } from '../components/SignalTable';

type Verdict = 'YES' | 'PARTLY' | 'NO';
/** INT-ANL-04 / D26: how the author judged each call at the end of the day, shown on every card of that analysis. */
const VERDICT: Record<Verdict, { bias: string; cls: string }> = {
  YES: { bias: 'Bias right', cls: 'border-[#1e5a3c] bg-up-bg text-up' },
  PARTLY: { bias: 'Bias partly right', cls: 'border-[#5a4316] bg-warn-bg text-warn' },
  NO: { bias: 'Bias wrong', cls: 'border-[#6a2a27] bg-down-bg text-down' },
};
const LEVELS: Record<Verdict, string> = { YES: 'levels held', PARTLY: 'levels partly held', NO: 'levels failed' };

interface FeedItem {
  id: string;
  type: string;
  createdAt: string;
  actor: { id: string; name: string };
  market: { id: string; code: string } | null;
  instrument: { id: string; symbol: string } | null;
  analysis: { id: string; tradingDay: string; isLate: boolean; version: number; sections: { symbol: string; noView: boolean; bias: string | null; confidence: string | null; keyLevels: { label: string; price: string }[]; review: { biasCorrect: Verdict | null; levelsRespected: Verdict | null; notes: string | null } | null }[] } | null;
  trade: TradeView | null;
  progressReport: { sequence: number | null; thesis: string; action: string; notes: string | null; isLate: boolean; unrealisedR: string | null; onBehalfOfId: string | null } | null;
  closingReport: { followedPlan: boolean; wentWell: string | null; improve: string | null } | null;
  social: (SocialSummary & { target: { type: TargetType; id: string }; sections?: Record<string, { agree: number; disagree: number; mine: string[] }> }) | null;
  setup?: { id: string; owner: { id: string; name: string }; direction: 'LONG' | 'SHORT'; timeframe: string; status: SetupStatus; zoneLow: string | null; zoneHigh: string | null; invalidationPrice: string | null; triggerCondition: string; targetIdea: string | null; chartUrl: string | null } | null;
  setupUpdate?: { id: string; status: SetupStatus; whatChanged: string; distanceNote: string | null; confidence: string | null; screenshotUrl: string | null; onBehalfOf: { id: string; name: string } | null } | null;
}

const PAGE_SIZE = 30;
const BIAS: Record<string, { label: string; cls: string }> = {
  BULLISH: { label: '▲ Bullish', cls: 'text-up' },
  BEARISH: { label: '▼ Bearish', cls: 'text-down' },
  NEUTRAL: { label: '■ Neutral', cls: 'text-sub' },
  NO_VIEW: { label: 'No view', cls: 'text-muted' },
};
const TYPES = [
  ['', 'Everything'],
  ['analyses', 'Analyses'],
  ['trades', 'Trades'],
  ['reports', 'Reports'],
  ['setups', 'Setups'],
  ['signals', 'Signals'],
  ['library', 'Library'],
] as const;

type Filters = { tab: string; market: string; symbol: string; trader: string; type: string; q: string };
type SetFilters = (patch: Partial<Filters>) => void;

function Fact({ k, v, cls }: { k: string; v: string; cls?: string }) {
  return (
    <span className="rounded-lg bg-raised px-2.5 py-1.5 text-[13px]">
      <span className="text-muted">{k}</span> <span className={cx('font-mono font-semibold', cls)}>{v}</span>
    </span>
  );
}

function Item({ i, tz, set, instrumentId }: { i: FeedItem; tz: string; set: SetFilters; instrumentId: (marketId: string, symbol: string) => string | undefined }) {
  const me = useMe();
  const [open, setOpen] = useState(false);
  const threadId = useId();
  const t = i.trade;
  const st = i.setup;
  // INT-PRG-05: reports and updates posted by a cover read "… on Ayesha's EURUSD short (covering)"
  const forOwner = (i.progressReport?.onBehalfOfId && t ? t.owner.name : null) ?? i.setupUpdate?.onBehalfOf?.name ?? null;
  const on = forOwner ? `${forOwner}'s ` : '';
  const verb =
    i.type === 'ANALYSIS_PUBLISHED' ? `published the ${i.market?.code} daily analysis` :
    i.type === 'ANALYSIS_EDITED' ? `edited the ${i.market?.code} daily analysis` :
    i.type === 'ANALYSIS_REVIEWED' ? `reviewed the ${i.market?.code} analysis for ${i.analysis?.tradingDay ?? 'a past day'}` :
    i.type === 'TRADE_OPENED' ? 'opened' :
    i.type === 'TRADE_CLOSED' ? 'closed' :
    i.type === 'SETUP_CREATED' ? 'marked a setup on' :
    i.type === 'SETUP_UPDATED' ? (i.setupUpdate?.status === 'CONVERTED' ? 'turned the setup on' : `updated ${on || 'the '}setup on`) :
    i.type === 'PROGRESS_REPORTED' ? (i.progressReport?.sequence ? `posted progress report #${i.progressReport.sequence} on ${on}` : `posted an ad-hoc report on ${on}`) : titleCase(i.type);
  const s = i.social;
  const ownPost = i.actor.id === me.data?.id || t?.owner.id === me.data?.id || st?.owner.id === me.data?.id;
  const what = i.analysis
    ? `${i.actor.name}'s ${i.market?.code} analysis`
    : t
      ? `${t.owner.name}'s ${t.symbol} ${t.direction === 'LONG' ? 'long' : 'short'}`
      : st
        ? `${st.owner.name}'s ${i.instrument?.symbol ?? ''} ${st.direction === 'LONG' ? 'long' : 'short'} setup`
        : 'this post';
  return (
    <article aria-label={`${i.actor.name} ${verb}${t ? ` ${t.symbol}` : ''}`} className="flex flex-col gap-3 rounded-xl border border-line bg-panel p-4">
      <div className="flex flex-wrap justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <Avatar name={i.actor.name} />
          <div className="flex min-w-0 flex-col">
            <span>
              <strong>
                <EntityMenu type="USER" id={i.actor.id} label={i.actor.name} onFilter={() => set({ trader: i.actor.id })} />
              </strong>{' '}
              <span className="text-sub">{verb}</span>{' '}
              {t && (
                <strong>
                  <EntityMenu type="INSTRUMENT" id={t.instrumentId} label={t.symbol} onFilter={() => set({ symbol: t.instrumentId })} /> {t.direction === 'LONG' ? 'long' : 'short'}
                </strong>
              )}
              {!t && st && i.instrument && (
                <strong>
                  <EntityMenu type="INSTRUMENT" id={i.instrument.id} label={i.instrument.symbol} onFilter={() => set({ symbol: i.instrument!.id })} /> {st.direction === 'LONG' ? 'long' : 'short'}
                </strong>
              )}
              {i.setupUpdate?.status === 'CONVERTED' && <span className="text-sub"> into a trade plan</span>}
              {forOwner && <span className="text-sub"> (covering)</span>}
            </span>
            <span className="text-xs text-muted">
              {fmtTime(i.createdAt, tz, true)}
              {i.market && (
                <>
                  {' · '}
                  <EntityMenu type="MARKET" id={i.market.id} label={i.market.code} onFilter={() => set({ market: i.market!.id })} className="text-xs" />
                </>
              )}
              {i.analysis?.isLate && ' · late'} {i.progressReport?.isLate && ' · late'}
            </span>
          </div>
        </div>
        <span className="self-start rounded-md border border-line2 bg-raised px-2 py-0.5 text-xs text-sub">{i.type === 'ANALYSIS_REVIEWED' ? 'Review' : titleCase(i.type.replace('_PUBLISHED', '').replace('PROGRESS_REPORTED', 'Report'))}</span>
      </div>

      {i.analysis && (
        <>
          <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
            {i.analysis.sections.map((sec) => {
              const b = BIAS[sec.noView ? 'NO_VIEW' : sec.bias ?? 'NO_VIEW']!;
              const instId = i.market ? instrumentId(i.market.id, sec.symbol) : undefined;
              const votes = s?.sections?.[sec.symbol];
              return (
                <div key={sec.symbol} className="flex flex-col gap-1 rounded-lg bg-raised p-2.5">
                  <span className="font-semibold">{instId ? <EntityMenu type="INSTRUMENT" id={instId} label={sec.symbol} onFilter={() => set({ symbol: instId })} /> : sec.symbol}</span>
                  <span className={cx('text-[13px] font-semibold', b.cls)}>
                    {b.label}
                    {sec.confidence ? ` · ${sec.confidence.toLowerCase()}` : ''}
                  </span>
                  <span className="text-xs text-muted">{sec.keyLevels.slice(0, 2).map((l) => `${l.label} ${l.price}`).join(' · ') || '—'}</span>
                  {sec.review?.biasCorrect && (
                    <span className={cx('self-start rounded border px-1.5 py-px text-[11px] font-semibold', VERDICT[sec.review.biasCorrect].cls)}>
                      {VERDICT[sec.review.biasCorrect].bias}
                      {sec.review.levelsRespected ? ` · ${LEVELS[sec.review.levelsRespected]}` : ''}
                    </span>
                  )}
                  {votes && votes.agree + votes.disagree > 0 && (
                    <span className="text-xs text-sub">
                      <span aria-hidden="true">
                        <span className="text-up">▲ {votes.agree}</span> · <span className="text-down">▼ {votes.disagree}</span>
                      </span>
                      <span className="sr-only">
                        {votes.agree} agree, {votes.disagree} disagree
                      </span>
                    </span>
                  )}
                </div>
              );
            })}
          </div>
          <Link to={`/analyses/${i.analysis.id}`} className="self-start font-semibold">
            Open analysis →
          </Link>
        </>
      )}

      {t && i.type === 'TRADE_OPENED' && (
        <div className="flex flex-wrap gap-2">
          <Fact k="Side" v={dirLabel(t.direction)} cls={dirClass(t.direction)} />
          <Fact k="Entry" v={t.avgEntry} />
          <Fact k="Stop" v={t.stopLoss ?? '—'} />
          <Fact k="Target" v={t.takeProfits[0]?.price ?? '—'} />
          {t.isUnplanned && <Fact k="Plan" v="Unplanned" cls="text-warn" />}
        </div>
      )}

      {i.progressReport && (
        <>
          <div className="flex flex-wrap gap-2">
            <Fact k="Open" v={fmtR(i.progressReport.unrealisedR)} cls={Number(i.progressReport.unrealisedR) >= 0 ? 'text-up' : 'text-down'} />
            <Fact k="Thesis" v={titleCase(i.progressReport.thesis)} cls={i.progressReport.thesis === 'VALID' ? '' : i.progressReport.thesis === 'WEAKENING' ? 'text-warn' : 'text-down'} />
            <Fact k="Action" v={titleCase(i.progressReport.action)} />
          </div>
          {i.progressReport.notes && <p className="m-0 leading-relaxed text-ink2">{i.progressReport.notes}</p>}
        </>
      )}

      {t && i.type === 'TRADE_CLOSED' && (
        <>
          <div className="flex flex-wrap gap-2">
            <Fact k="Result" v={fmtR(t.realisedR)} cls={Number(t.realisedR) >= 0 ? 'text-up' : 'text-down'} />
            {i.closingReport && <Fact k="Followed plan" v={i.closingReport.followedPlan ? 'Yes' : 'No'} />}
          </div>
          {i.closingReport?.improve && <p className="m-0 leading-relaxed text-ink2">{i.closingReport.improve}</p>}
        </>
      )}
      {t && (
        <Link to={i.progressReport && s ? `/trades/${t.id}?report=${s.target.id}` : `/trades/${t.id}`} className="self-start text-sm">
          View trade →
        </Link>
      )}

      {st && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <SetupStatusPill status={i.setupUpdate?.status ?? 'WATCHING'} />
            <Fact k="Side" v={dirLabel(st.direction)} cls={dirClass(st.direction)} />
            <Fact k="Zone" v={zoneText(st)} />
            <Fact k="Invalidation" v={st.invalidationPrice ?? '—'} cls="text-down" />
            <Fact k="TF" v={st.timeframe} />
          </div>
          {i.setupUpdate ? (
            i.setupUpdate.status !== 'CONVERTED' && (
              <div className="flex flex-col gap-1">
                <p className="m-0 leading-relaxed text-ink2">{i.setupUpdate.whatChanged}</p>
                {i.setupUpdate.distanceNote && <span className="text-sm text-sub">{i.setupUpdate.distanceNote}</span>}
              </div>
            )
          ) : (
            <p className="m-0 leading-relaxed text-ink2">
              <span className="text-muted">Trigger: </span>
              {st.triggerCondition}
              {st.targetIdea && (
                <>
                  <span className="text-muted"> · Target: </span>
                  {st.targetIdea}
                </>
              )}
            </p>
          )}
          <div className="flex flex-wrap gap-4 text-sm">
            <Link to={`/setups/${st.id}`} className="font-semibold">
              Open setup →
            </Link>
            {(i.setupUpdate?.screenshotUrl ?? (!i.setupUpdate ? st.chartUrl : null)) && (
              <a href={(i.setupUpdate?.screenshotUrl ?? st.chartUrl)!} target="_blank" rel="noreferrer noopener">
                Chart ↗
              </a>
            )}
          </div>
        </>
      )}

      {s && (
        <div className="flex flex-col gap-3 border-t border-line pt-3">
          <div className="flex flex-wrap items-center gap-1.5">
            <ReactionBar targetType={s.target.type} targetId={s.target.id} kinds={['LIKE']} summary={s} readOnly={ownPost} what={what} />
            <DiscussToggle count={s.comments} open={open} onClick={() => setOpen((v) => !v)} controls={threadId} />
          </div>
          {open && (
            <div id={threadId}>
              <CommentThread targetType={s.target.type} targetId={s.target.id} what={what} autoFocus />
              {i.analysis && s.comments > 0 && (
                <p className="m-0 mt-2 text-xs text-muted">
                  Symbol-by-symbol discussion is on the <Link to={`/analyses/${i.analysis.id}`}>analysis page</Link>.
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </article>
  );
}

function Chip({ children, onRemove, label }: { children: ReactNode; onRemove: () => void; label: string }) {
  return (
    <span className="inline-flex min-h-8 items-center gap-1 rounded-full border border-line2 bg-raised py-0.5 pr-1 pl-3 text-[13px]">
      {children}
      <button type="button" aria-label={label} onClick={onRemove} className="flex h-6 w-6 cursor-pointer items-center justify-center rounded-full border-0 bg-transparent text-muted hover:bg-[#232a34] hover:text-ink">
        ✕
      </button>
    </span>
  );
}

/** Floor → Signals as cards or as the table (INT-SYN-02); each viewer's choice is remembered on this device. */
const SIGNALS_LAYOUT_KEY = 'vtf.floor.signals.layout';
type SignalsLayout = 'cards' | 'table';
function useSignalsLayout(): [SignalsLayout, (v: SignalsLayout) => void] {
  const [layout, setLayout] = useState<SignalsLayout>(() => {
    try {
      return localStorage.getItem(SIGNALS_LAYOUT_KEY) === 'table' ? 'table' : 'cards';
    } catch {
      return 'cards';
    }
  });
  const set = (v: SignalsLayout) => {
    setLayout(v);
    try {
      localStorage.setItem(SIGNALS_LAYOUT_KEY, v);
    } catch {
      /* storage blocked: the choice lasts for this visit only */
    }
  };
  return [layout, set];
}

function FollowingBar() {
  const f = useFollows();
  if (!f.data) return null;
  if (!f.data.length)
    return (
      <Card className="p-4 text-sub">
        You aren't following anyone yet. Select a trader's name, a symbol or a market on the floor and choose <strong className="text-ink">Follow</strong>. You'll see their posts here and get an alert when they publish an analysis or open a trade.
      </Card>
    );
  return (
    <div role="group" className="flex flex-wrap items-center gap-1.5" aria-label="What you follow">
      <span className="mr-1 text-xs tracking-[0.06em] text-muted uppercase">You follow</span>
      {f.data.map((x) => (
        <Chip key={`${x.targetType}:${x.targetId}`} label={`Unfollow ${x.label}`} onRemove={() => f.set.mutate({ targetType: x.targetType, targetId: x.targetId, on: false })}>
          <span className="font-semibold">{x.label}</span>
          {x.targetType === 'INSTRUMENT' && x.sub && <span className="text-xs text-muted">{x.sub}</span>}
        </Chip>
      ))}
    </div>
  );
}

export function Floor() {
  const me = useMe();
  const tz = me.data?.timezone ?? 'UTC';
  const [params, setParams] = useSearchParams();
  const fl: Filters = {
    tab: params.get('tab') === 'following' ? 'following' : 'all',
    market: params.get('market') ?? '',
    symbol: params.get('symbol') ?? '',
    trader: params.get('trader') ?? '',
    type: params.get('type') ?? '',
    q: params.get('q') ?? '',
  };
  // Filters live in the URL (shareable, survive reloads). Patch the *current* URL: the router's functional updater can
  // hand back a stale copy when updates come quickly (e.g. the search debounce firing after "Clear all").
  const set: SetFilters = (patch) => {
    const n = new URLSearchParams(window.location.search);
    for (const [k, v] of Object.entries(patch)) {
      if (v && !(k === 'tab' && v === 'all')) n.set(k, v);
      else n.delete(k);
    }
    setParams(n, { replace: true });
  };
  const [search, setSearch] = useState(fl.q);
  useEffect(() => {
    const t = setTimeout(() => {
      if (search.trim() !== (new URLSearchParams(window.location.search).get('q') ?? '')) set({ q: search.trim() });
    }, 350);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  const { data: markets } = useQuery({ queryKey: ['markets'], queryFn: () => api<{ id: string; code: string; name: string }[]>('/markets') });
  const { data: instruments } = useQuery({ queryKey: ['instruments'], queryFn: () => api<Instrument[]>('/instruments'), staleTime: 300_000 });
  const members = useMembers();
  const bySymbol = useMemo(() => new Map((instruments ?? []).map((x) => [`${x.marketId}:${x.symbol}`, x.id])), [instruments]);
  const instrumentId = (marketId: string, symbol: string) => bySymbol.get(`${marketId}:${symbol}`);
  const marketCode = (id: string) => markets?.find((m) => m.id === id)?.code ?? '';

  const feed = useInfiniteQuery({
    queryKey: ['feed', fl.tab, fl.market, fl.symbol, fl.trader, fl.type, fl.q],
    initialPageParam: null as { before: string; beforeId: string } | null,
    queryFn: ({ pageParam }) =>
      api<FeedItem[]>(
        `/feed?${new URLSearchParams({
          tab: fl.tab,
          ...(fl.market ? { marketId: fl.market } : {}),
          ...(fl.symbol ? { instrumentId: fl.symbol } : {}),
          ...(fl.trader ? { traderId: fl.trader } : {}),
          ...(fl.type ? { type: fl.type } : {}),
          ...(fl.q ? { q: fl.q } : {}),
          ...(pageParam ?? {}),
        })}`,
      ),
    getNextPageParam: (last) => (last.length === PAGE_SIZE ? { before: last.at(-1)!.createdAt, beforeId: last.at(-1)!.id } : undefined),
  });
  const items = feed.data?.pages.flat() ?? [];
  const symbolOptions = (instruments ?? []).filter((x) => !fl.market || x.marketId === fl.market);
  const traderName = members.data?.find((m) => m.id === fl.trader)?.name;
  const symbolName = instruments?.find((x) => x.id === fl.symbol)?.symbol;
  const filtered = !!(fl.market || fl.symbol || fl.trader || fl.type || fl.q);
  // D27: new signals flash until their flashUntil; one timer re-renders at the next expiry, no reload needed
  const signals = items.flatMap((i) => (isSignalFeed(i) ? [i.signal] : []));
  const now = useFlashClock(signals.map((s) => s.flashUntil));
  const newSignals = signals.filter((s) => isFlashing(s, now)).length;
  const [layout, setLayout] = useSignalsLayout();
  const asTable = fl.type === 'signals' && layout === 'table';

  return (
    <>
      <PageHeader
        title="Floor"
        sub="Everything published on the floor · live"
        right={
          <div role="tablist" aria-label="Feed" className="flex rounded-xl border border-line2 bg-panel p-1">
            {[
              ['all', 'All'],
              ['following', 'Following'],
            ].map(([v, l]) => (
              <button key={v} type="button" role="tab" aria-selected={fl.tab === v} aria-controls="floor-feed" onClick={() => set({ tab: v })} className={cx('min-h-9 cursor-pointer rounded-lg border-0 px-4 py-1.5 text-sm', fl.tab === v ? 'bg-accent font-bold text-bg' : 'bg-transparent text-sub hover:text-ink')}>
                {l}
              </button>
            ))}
          </div>
        }
      />

      <div className="flex max-w-[900px] flex-col gap-3">
        {/* the table filters by market, timeframe, strategy and event; search, symbol and trader apply to the cards */}
        <div hidden={asTable} className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_170px_190px]">
          <label className="relative block">
            <span className="sr-only">Search the floor</span>
            <svg aria-hidden="true" className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-muted" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <circle cx="11" cy="11" r="7" />
              <path d="m20 20-3.5-3.5" />
            </svg>
            <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search symbols, traders, notes…" className="w-full rounded-lg border border-line2 bg-field py-2.5 pr-3 pl-9 text-ink placeholder:text-muted" />
          </label>
          {/* option names go in the `label` attribute: shown in the list, but not repeated in the page's text (find-on-page and text locators land on the feed) */}
          <Select aria-label="Symbol" value={fl.symbol} onChange={(e) => set({ symbol: e.target.value })}>
            <option value="" label="All symbols" />
            {(markets ?? []).map((m) => {
              const opts = symbolOptions.filter((x) => x.marketId === m.id);
              return opts.length ? (
                <optgroup key={m.id} label={m.code}>
                  {opts.map((x) => (
                    <option key={x.id} value={x.id} label={x.symbol} />
                  ))}
                </optgroup>
              ) : null;
            })}
          </Select>
          <Select aria-label="Trader" value={fl.trader} onChange={(e) => set({ trader: e.target.value })}>
            <option value="" label="All traders" />
            {(members.data ?? []).map((m) => (
              <option key={m.id} value={m.id} label={m.name} />
            ))}
          </Select>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div role="group" aria-label="Filter by market" className="flex flex-wrap gap-1.5">
            {[{ id: '', code: 'All markets' }, ...(markets ?? [])].map((m) => (
              <button key={m.id} type="button" aria-pressed={fl.market === m.id} onClick={() => set({ market: m.id, ...(m.id && fl.symbol && instruments?.find((x) => x.id === fl.symbol)?.marketId !== m.id ? { symbol: '' } : {}) })} className={cx('min-h-9 cursor-pointer rounded-full px-3.5 py-1.5', fl.market === m.id ? 'border-0 bg-accent font-semibold text-bg' : 'border border-line2 bg-panel text-sub')}>
                {m.code}
              </button>
            ))}
          </div>
          <div role="group" aria-label="Filter by type" className="flex flex-wrap gap-1.5">
            {TYPES.map(([v, l]) => (
              <button key={v} type="button" aria-pressed={fl.type === v} title={v === 'signals' && newSignals > 0 ? `${newSignals} new signal${newSignals === 1 ? '' : 's'}` : undefined} onClick={() => set({ type: v })} className={cx('min-h-8 cursor-pointer rounded-lg border border-line2 px-3 py-1 text-[13px]', fl.type === v ? 'bg-[#1a1f27] text-ink' : 'bg-panel text-sub', v === 'signals' && newSignals > 0 && 'signal-flash-chip')}>
                {l}
                {v === 'signals' && newSignals > 0 && (
                  <span aria-hidden="true" data-testid="new-signals" className="ml-1.5 rounded-full bg-accent px-1.5 font-mono text-[11px] font-bold text-bg">
                    {newSignals}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {filtered && (
          <div role="group" className="flex flex-wrap items-center gap-1.5" aria-label="Active filters">
            <span className="mr-1 text-xs tracking-[0.06em] text-muted uppercase">Showing</span>
            {fl.trader && traderName && (
              <Chip label={`Remove trader filter ${traderName}`} onRemove={() => set({ trader: '' })}>
                <span className="font-semibold">{traderName}</span>
              </Chip>
            )}
            {fl.symbol && symbolName && (
              <Chip label={`Remove symbol filter ${symbolName}`} onRemove={() => set({ symbol: '' })}>
                <span className="font-semibold">{symbolName}</span>
              </Chip>
            )}
            {fl.market && (
              <Chip label={`Remove market filter ${marketCode(fl.market)}`} onRemove={() => set({ market: '' })}>
                <span className="font-semibold">{marketCode(fl.market)}</span>
              </Chip>
            )}
            {fl.type && (
              <Chip label="Remove type filter" onRemove={() => set({ type: '' })}>
                {TYPES.find(([v]) => v === fl.type)?.[1]}
              </Chip>
            )}
            {fl.q && (
              <Chip label="Clear search" onRemove={() => (setSearch(''), set({ q: '' }))}>
                “{fl.q}”
              </Chip>
            )}
            {fl.trader && traderName && <FollowButton targetType="USER" targetId={fl.trader} label={traderName} />}
            {fl.symbol && symbolName && <FollowButton targetType="INSTRUMENT" targetId={fl.symbol} label={symbolName} />}
            {!fl.trader && !fl.symbol && fl.market && <FollowButton targetType="MARKET" targetId={fl.market} label={marketCode(fl.market)} />}
            <button type="button" onClick={() => (setSearch(''), set({ market: '', symbol: '', trader: '', type: '', q: '' }))} className="ml-1 cursor-pointer border-0 bg-transparent text-[13px] font-semibold text-accent">
              Clear all
            </button>
          </div>
        )}

        {fl.type === 'signals' && (
          <Segmented
            label="Signals layout"
            value={layout}
            onChange={setLayout}
            options={[
              { value: 'cards', label: 'Cards' },
              { value: 'table', label: 'Table' },
            ]}
          />
        )}
        {fl.tab === 'following' && !asTable && <FollowingBar />}
        {!asTable && <span className="text-xs text-muted">Select a name, symbol or market to follow it or show only its posts · money amounts are hidden on the floor (R and % shown)</span>}
      </div>

      {asTable && <SignalTable market={fl.market} tz={tz} />}
      <section hidden={asTable} id="floor-feed" role="tabpanel" aria-label={fl.tab === 'following' ? 'Following feed' : 'Feed'} className="flex max-w-[900px] flex-col gap-3">
        <ErrorText error={feed.error ? errorText(feed.error) : null} />
        {feed.isLoading && <Card className="p-4 text-sub">Loading…</Card>}
        {!feed.isLoading && !items.length && (
          <Card>
            <Empty>{filtered ? 'Nothing matches these filters.' : fl.tab === 'following' ? 'Nothing from what you follow yet.' : 'Nothing on the floor yet.'}</Empty>
          </Card>
        )}
        {items.map((i) =>
          isLibraryFeed(i) ? (
            <LibraryFeedItem key={i.id} item={i} tz={tz} />
          ) : isSignalFeed(i) ? (
            <SignalFeedItem key={i.id} item={i} tz={tz} now={now} onSymbol={(id) => set({ symbol: id })} />
          ) : (
            <Item key={i.id} i={i} tz={tz} set={set} instrumentId={instrumentId} />
          ),
        )}
        {feed.hasNextPage && (
          <Button className="self-center" onClick={() => void feed.fetchNextPage()} disabled={feed.isFetchingNextPage}>
            {feed.isFetchingNextPage ? 'Loading…' : 'Show older posts'}
          </Button>
        )}
      </section>
    </>
  );
}
